import "./styles/app.css";
import "./styles/date-selector.css";
import { createContext, useCallback, useEffect, useState } from "react";
import { eventResultStatusCodesMapfre, routes } from "./utils/symbols";
import { BookingForm } from "./pages/BookingForm";
import { Intro } from "./pages/Intro";
import { SelectCenter } from "./pages/SelectCenter";
import { DamageGlass } from "./pages/DamageGlass";
import { DamageType } from "./pages/DamageType";
import { DamageArea } from "./pages/DamageArea";
import { SelectDateTime } from "./pages/SelectDateTime";
import { BookingEnd } from "./pages/BookingEnd";
import { ThankYouPage } from "./pages/ThankYouPage";
import { Loader } from "./pages/Loader";
import { sendClarityEvent, sendGTMEvent } from "./utils/analytics";
import { ContactForm } from "./pages/ContactForm";
import { ErrorPage } from "./pages/ErrorPage";
import { getInitialData, sendEventToParent } from "./utils/helpers";
import { DamageGlassMoreInfo } from "./pages/DamageGlassMoreInfo";

export const AppContext = createContext();

function App() {
	const flow = process.env.REACT_APP_FLOW || "web";
	const [route, navigate] = useState(routes.intro);

	const [data, setData] = useState(getInitialData());

	const [centers, setCenters] = useState([]);
	const [filteredCenters, setFilteredCenters] = useState([]);
	const [visibleCenters, setVisibleCenters] = useState([]);
	const [geolocationFailed, setGeolocationFailed] = useState(false);
	const [bookingSent, setBookingSent] = useState(false);

	const [companies, setCompanies] = useState([]);

	const [brands, setBrands] = useState([]);
	const [models, setModels] = useState([]);

	const [promos, setPromos] = useState([]);

	const [isTransitioning, setIsTransitioning] = useState(false);

	const [popUpClientEdit, setPopUpClientEdit] = useState(false);
	const [popUpCall, setPopUpCall] = useState(false);
	const [popUpError, setPopUpError] = useState(false);
	const [viewMap, setViewMap] = useState(null);

	const [errors, setErrors] = useState({});

	const [progressData, setProgressData] = useState({
		progress: 0,
		prevProgress: 0,
		isAnimating: false,
	});

	const params = new URLSearchParams(window.location.search);
	const centerCodeParam = params.get("centerCode");

	const updateProgress = (newProgress) => {
		setProgressData((prev) => ({
			prevProgress: prev.progress,
			progress: newProgress,
			isAnimating: true,
		}));
		setTimeout(() => {
			setProgressData((prev) => ({ ...prev, isAnimating: false }));
		}, 600);
	};

	const openPopUpClientEdit = () => {
		setPopUpClientEdit(true);
	};

	const closePopUpClientEdit = () => {
		setPopUpClientEdit(false);
	};

	const openPopUpCall = () => {
		setPopUpCall(true);
	};

	const closePopUpCall = () => {
		setPopUpCall(false);
	};

	const openPopUpError = () => {
		setPopUpError(true);
	};

	const closePopUpError = () => {
		setPopUpError(false);
	};

	const validateForm = useCallback(() => {
		let newErrors = {};

		const modernFormat = /^[0-9]{4}[BCDFGHJKLMNPRSTVWXYZ]{3}$/i;
		const oldFormat = /^[A-Z]{1,2}\d{4}[A-Z]{0,2}$/i;
		const veryOldFormat = /^[A-Z]{1,2}\d{1,6}$/i;
		const insuranceError = "Selecciona una aseguradora"

		if (route === routes.bookingForm) {
			if (
				(data.userName &&
					!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(
						data.userName.trim(),
					)) ||
				(data.name &&
					!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(data.name.trim()))
			)
				newErrors.name = "Nombre no válido";
			if (
				flow === "mapfre"
					? data.phoneNumber &&
					  !/^[6789]\d{8}$/.test(data.phoneNumber)
					: data.phone && !/^\+[1-9]\d{7,14}$/.test(data.phone)
			)
				newErrors.phone = "El teléfono no es válido";
			if (
				(data.userEmail && !/^\S+@\S+\.\S+$/.test(data.userEmail)) ||
				(data.email && !/^\S+@\S+\.\S+$/.test(data.email))
			)
				newErrors.email = "Email no válido";
			if (
				(data.plateNumber &&
					!(
						modernFormat.test(data.plateNumber) ||
						oldFormat.test(data.plateNumber) ||
						veryOldFormat.test(data.plateNumber)
					)) ||
				(data.plate &&
					!(
						modernFormat.test(data.plate) ||
						oldFormat.test(data.plate) ||
						veryOldFormat.test(data.plate)
					))
			)
				newErrors.plate = "Matrícula no válida";
			if (data.workCenterCode && !data.workCenterCode)
				newErrors.workCenterCode = "Debes seleccionar un taller";
			if (data.brand && !data.brand.trim())
				newErrors.brand = "La marca es obligatoria";
			if (data.model && !data.model.trim())
				newErrors.model = "El modelo es obligatorio";
		} else if (route === routes.contactForm) {
			if (
				data.name &&
				!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(data.name.trim())
			)
				newErrors.name = "Nombre no válido";
			if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
				newErrors.email = "Email no válido";
			if (data.phone && !/^\+[1-9]\d{7,14}$/.test(data.phone))
				newErrors.phone = "El teléfono no es válido";
			if (
				data.postalCode &&
				!/^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/.test(data.postalCode)
			)
				newErrors.postalCode = "Código postal no válido";
			if (data.message && !data.message.trim())
				newErrors.message = "El mensaje es obligatorio";
		} else if (route === routes.bookingEnd) {
			if (!data.plate)
				newErrors.plate = "La matrícula es obligatoria";
			else if (
				!(
					modernFormat.test(data.plate) ||
					oldFormat.test(data.plate) ||
					veryOldFormat.test(data.plate)
				)
			)
				newErrors.plate = "Matrícula no válida";
			if (!data.brand || !data.brand.trim())
				newErrors.brand = "La marca es obligatoria";
			if (!data.model || !data.model.trim())
				newErrors.model = "El modelo es obligatorio";
			if (!data.email)
				newErrors.email = "El email es obligatorio";
			else if (!/^\S+@\S+\.\S+$/.test(data.email))
				newErrors.email = "Email no válido";
			if (!data.name || !data.name.trim())
				newErrors.name = "El nombre es obligatorio";
			else if (
				!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(data.name.trim())
			)
				newErrors.name = "Nombre no válido";
			if (!data.phone)
				newErrors.phone = "El teléfono es obligatorio";
			else if (!/^\+[1-9]\d{7,14}$/.test(data.phone))
				newErrors.phone = "El teléfono no es válido";
			if (
				flow === "web" &&
				((!data.noInsurance && !data.insurance) ||
					data.insurance.toLowerCase() === "web")
			) {
				newErrors.insurance = insuranceError;
			}
			if (!data.privacy_policy_2)
				newErrors.privacy_policy_2 =
					"Debes aceptar la política de privacidad";
		} else {
			if (
				data.name &&
				!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(data.name.trim())
			)
				newErrors.name = "Nombre no válido";
			if (data.phone && !/^\+[1-9]\d{7,14}$/.test(data.phone))
				newErrors.phone = "El teléfono no es válido";
			if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
				newErrors.email = "Email no válido";
			if (
				data.plate &&
				!(
					modernFormat.test(data.plate) ||
					oldFormat.test(data.plate) ||
					veryOldFormat.test(data.plate)
				)
			)
				newErrors.plate = "Matrícula no válida";
			if (
				flow === "web" &&
				((!data.noInsurance && !data.insurance) ||
					data.insurance.toLowerCase() === "web")
			) {
				newErrors.insurance = insuranceError;
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [route, data, flow]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const locator = params.get("locator");

		const origin = getOrigin(flow, params.get("origin"));
		if (origin) {
			console.log("App.jsx - Origin from Param:", origin);
			setData((prevData) => ({
				...prevData,
				origin: origin,
			}));
		} else {
			// If no origin in URL params, check cookies
			const cookieOrigin = (document.cookie.match(
				/(?:^|;\s*)rl_origin=([^;]*)/,
			) || [])[1];
			if (cookieOrigin) {
				console.log("App.jsx - Origin from Cookie:", cookieOrigin);
				setData((prevData) => ({
					...prevData,
					origin: cookieOrigin,
				}));
			}
		}

		const rlid = params.get("rlid");
		if (rlid) {
			console.log("App.jsx - Rlid from Param:", rlid);
			setData((prevData) => ({
				...prevData,
				rlid: rlid,
			}));
		} else {
			// If no rlid in URL params, check cookies
			const cookieRlid = (document.cookie.match(
				/(?:^|;\s*)rlid=([^;]*)/,
			) || [])[1];
			if (cookieRlid) {
				console.log("App.jsx - Rlid from Cookie:", cookieRlid);
				setData((prevData) => ({
					...prevData,
					rlid: cookieRlid,
				}));
			}
		}

		const journey = params.get("rljourney");
		if (journey) {
			console.log("App.jsx - Journey from Param:", journey);
			setData((prevData) => ({
				...prevData,
				journey: journey,
			}));
		} else {
			// If no journey in URL params, check cookies
			const cookieJourney = (document.cookie.match(
				/(?:^|;\s*)r_journey=([^;]*)/,
			) || [])[1];
			if (cookieJourney) {
				console.log("App.jsx - Journey from Cookie:", cookieJourney);
				setData((prevData) => ({
					...prevData,
					journey: cookieJourney,
				}));
			}
		}

		if (centerCodeParam) {
			setData((prevData) => ({
				...prevData,
				centerCode: centerCodeParam,
				centerFromParam: true,
			}));
			sendClarityEvent("fc-taller-preseleccionado");
			sendGTMEvent("fc-taller-preseleccionado", {
				origin: data.origin,
				locator: data.locator,
			});
			if (flow !== "mapfre") {
				window.parent.postMessage(
					{
						type: "fc-taller-preseleccionado",
						data: {
							origin: data.origin,
						},
					},
					"*",
				);
			}
		}

		if (!locator) {
			navigate(routes.bookingForm);
		} else {
			setData((prevData) => ({
				...prevData,
				locator,
			}));
		}

		const popUpCall = params.get("popUpCall");
		const contactForm = params.get("contactForm");

		const location = params.get("location");

		const searchWorkcenters = params.get('searchWorkcenters')

		if (location || location === "") {
			navigate(routes.selectCenter);
			setViewMap(location);
		}

		if (popUpCall) {
			openPopUpCall();
		} else if (contactForm) {
			navigate(routes.contactForm);
		} else if (!location) {
			sendClarityEvent("fc-iniciar-cita");
			sendGTMEvent("fc-iniciar-cita", {
				origin: data.origin,
				locator: data.locator,
			});
			if (flow !== "mapfre") {
				window.parent.postMessage(
					{
						type: "fc-iniciar-cita",
						data: {
							origin: data.origin,
						},
					},
					"*",
				);
			}
		}

		if (searchWorkcenters){
			setData((prevData)=>({
				...prevData,
				searchWorkcenters: searchWorkcenters
			}))
			sendClarityEvent("fc-cita-urgente");
			sendGTMEvent("fc-cita-urgente", {
				origin: data.origin,
				locator: data.locator,
			});
			if (flow !== "mapfre") {
				window.parent.postMessage(
					{
						type: "fc-cita-urgente",
						data: {
							origin: data.origin,
						},
					},
					"*",
				);
			}
		}

		// Get active promos from WP
		const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
		const token = process.env.REACT_APP_API_TOKEN;
		const lang =
			new URLSearchParams(window.location.search).get("lang") || "es_ES";

		const controller = new AbortController();
		const options = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			signal: controller.signal,
			body: JSON.stringify({ token, lang }),
		};

		fetch(`${apiUrl}/query/promo`, options)
			.then((res) => res.json())
			.then((newData) => {
				if (newData.result === "ok") {
					setPromos(newData.data);
				}
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				controller.abort();
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!data.centerCode || !centerCodeParam) return;

		if (data.centerCode !== centerCodeParam) {
			setData((prev) => ({
				...prev,
				centerCode: centerCodeParam,
			}));
		}
	}, [data.centerCode, centerCodeParam]);

	useEffect(() => {
		// console.log(data);
		route !== routes.thankYouPage &&
			sessionStorage.setItem("Data", JSON.stringify(data)) &&
			sessionStorage.setItem("Data_expires", String(Date.now() + 3600000));
	}, [data, route]);

	useEffect(() => {
		let apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

		let controller = new AbortController();
		let options = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			signal: controller.signal,
		};

		if (data.locator) {
			fetch(`${apiUrl}/bookings/${data.locator}`, options)
				.then((res) => res.json())
				.then((newData) => {
					if (newData.result === "ok") {
						setData((prevData) => ({
							...prevData,
							...newData.booking,
							origin: prevData.origin,
							rlid: prevData.rlid,
							journey: prevData.journey,
							centerCode:
								prevData.centerCode ||
								newData.booking.centerCode,
							date: prevData.date ?? newData.booking.date,
							startTime:
								prevData.startTime ?? newData.booking.startTime,
							endTime:
								prevData.endTime ?? newData.booking.endTime,
							plate: prevData.plate ?? newData.booking.plate,
							plateNumber:
								prevData.plateNumber ?? newData.booking.plate,
							damageArea:
								prevData.damageArea ?? newData.booking.type,
						}));
					}
				})
				.catch((err) => {
					console.error(err);
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						flow,
					);
				})
				.finally(() => {
					controller.abort();
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.locator]);

	useEffect(() => {
		if (data.centerId && centers.length > 0) {
			const selectedCenter = centers.find(
				(center) => center.id === data.centerId,
			);
			if (selectedCenter) {
				setData((prevData) => ({
					...prevData,
					centerName: selectedCenter.name,
					centerCode: selectedCenter.code,
					centerAddress: selectedCenter.address,
				}));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.centerId, centers, data.journey]);

	useEffect(() => {
		setIsTransitioning(true);
	}, [data.date, route, setIsTransitioning]);

	return (
		<div
			className={`app ${flow} ${route} ${data.journey === "B" ? "journey_b" : ""} ${
				viewMap || viewMap === "" ? "site_map" : ""
			}`}
		>
			<AppContext.Provider
				value={{
					data,
					setData,
					centers,
					setCenters,
					filteredCenters,
					setFilteredCenters,
					visibleCenters,
					setVisibleCenters,
					geolocationFailed,
					setGeolocationFailed,
					bookingSent,
					setBookingSent,
					companies,
					setCompanies,
					brands,
					setBrands,
					models,
					setModels,
					promos,
					route,
					navigate,
					popUpClientEdit,
					openPopUpClientEdit,
					closePopUpClientEdit,
					popUpCall,
					openPopUpCall,
					closePopUpCall,
					popUpError,
					openPopUpError,
					closePopUpError,
					isTransitioning,
					setIsTransitioning,
					errors,
					validateForm,
					flow,
					viewMap,
					progressData,
					updateProgress,
				}}
			>
				{route === routes.bookingForm ? (
					<BookingForm />
				) : route === routes.intro ? (
					<Intro />
				) : route === routes.selectCenter ? (
					<SelectCenter />
				) : route === routes.damageGlass ? (
					<DamageGlass />
				) : route === routes.damageGlassMoreInfo ? (
					<DamageGlassMoreInfo />
				): route === routes.damageType ? (
					<DamageType />
				) : route === routes.damageArea ? (
					<DamageArea />
				) : route === routes.selectDateTime ? (
					<SelectDateTime />
				) : route === routes.bookingEnd ? (
					<BookingEnd />
				) : route === routes.thankYouPage ? (
					<ThankYouPage />
				) : route === routes.contactForm ? (
					<ContactForm />
				) : route === routes.errorPage ? (
					<ErrorPage />
				) : (
					<Loader />
				)}
			</AppContext.Provider>
		</div>
	);
}

function getOrigin(flow, origin) {
	if (flow === "mapfre") {
		return "app.mapfre";
	}
	if (flow === "generali") {
		if (origin) {
			return `site.generali.${origin}`;
		}
		return "site.generali";
	}
	return origin;
}

export default App;
